import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { api } from "../api/rest";
import type { PFBookResponse, PFOrder, PFOutcome, PFPlaceRequest, PFPosition, PFStatus } from "../api/types";

/**
 * Проба predict.fun (ADR-0040): опрос REST, как у сводки фида. Стакан —
 * состояние, которое сервис predictfun перекладывает в Redis раз в секунду;
 * сокет и новый тип сообщения ради пробы не заводятся.
 */

const BOOK_MS = 1000;
/**
 * Ордера — раз в три секунды: каждый опрос — запрос к площадке с бюджетом
 * 240 в минуту на ключ, который делят резолвер окон и опрос стаканов.
 */
const ORDERS_MS = 3000;
const STATUS_MS = 10_000;

export function usePFBook(slug: string | null) {
  return useQuery<PFBookResponse>({
    queryKey: ["pf", "book", slug],
    queryFn: () => api.pfBook(slug ?? ""),
    enabled: Boolean(slug),
    refetchInterval: BOOK_MS,
    refetchIntervalInBackground: false,
  });
}

export function usePFStatus(enabled = true) {
  return useQuery<PFStatus>({
    queryKey: ["pf", "status"],
    queryFn: api.pfStatus,
    enabled,
    refetchInterval: STATUS_MS,
    refetchIntervalInBackground: false,
    retry: false,
  });
}

export function usePFOrders(enabled = true) {
  return useQuery<{ orders: PFOrder[] }>({
    queryKey: ["pf", "orders"],
    queryFn: () => api.pfOrders("open"),
    enabled,
    refetchInterval: ORDERS_MS,
    refetchIntervalInBackground: false,
    retry: false,
  });
}

export function usePFPlace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: PFPlaceRequest) => api.pfPlace(req),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["pf", "orders"] });
      void qc.invalidateQueries({ queryKey: ["pf", "positions"] });
    },
  });
}

/** Шаг долгого опроса итога: сервис держит запрос до 25 с, прокси — 30. */
const OUTCOME_WAIT_S = 20;
/** Сколько всего ждать итога: тейкер сервис ждёт 15 с, дальше — PENDING. */
const OUTCOME_TOTAL_MS = 60_000;

/**
 * Ожидание итога ордера после 201 (ADR-0041): постановка отвечает сразу, а
 * «стоит», «исполнен» или «отвергнут» приходит долгим опросом. Опрос не
 * тратит бюджет ключа площадки — итог сервис узнаёт из ленты кошелька.
 */
export function usePFAwaitOutcome() {
  const qc = useQueryClient();
  return useCallback(
    async (orderId: string): Promise<PFOutcome> => {
      const until = Date.now() + OUTCOME_TOTAL_MS;
      let last = await api.pfResult(orderId, OUTCOME_WAIT_S);
      while (last.status === "ACCEPTED" && Date.now() < until) {
        last = await api.pfResult(orderId, OUTCOME_WAIT_S);
      }
      void qc.invalidateQueries({ queryKey: ["pf", "orders"] });
      void qc.invalidateQueries({ queryKey: ["pf", "positions"] });
      return last;
    },
    [qc],
  );
}

export function usePFCancelMarket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (slug: string) => api.pfCancelMarket(slug),
    onSettled: () => void qc.invalidateQueries({ queryKey: ["pf", "orders"] }),
  });
}

export function usePFCancel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.pfCancel(id),
    onSettled: () => void qc.invalidateQueries({ queryKey: ["pf", "orders"] }),
  });
}

/**
 * Позиции кошелька по окну — для строки PF быстрых действий. Тем же шагом,
 * что ордера: это тот же бюджет ключа площадки.
 */
export function usePFPositions(slug: string | null, enabled = true) {
  return useQuery<{ positions: PFPosition[] }>({
    queryKey: ["pf", "positions", slug],
    queryFn: () => api.pfPositions(slug ?? ""),
    enabled: enabled && Boolean(slug),
    refetchInterval: ORDERS_MS,
    refetchIntervalInBackground: false,
    retry: false,
  });
}
